import { useState, useEffect, useRef } from "react"
import { Loader2, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { ImageUpload } from "./ImageUpload"
import { useQueryClient } from "@tanstack/react-query"

export const PostForm = () => {
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [prompt, setPrompt] = useState("")
  const [caption, setCaption] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [touched, setTouched] = useState({
    image: false,
    prompt: false,
  })
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const captionRef = useRef<HTMLTextAreaElement>(null)

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target
    adjustTextareaHeight(textarea)
    setPrompt(textarea.value)
    setTouched(prev => ({ ...prev, prompt: true }))
  }

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target
    adjustTextareaHeight(textarea)
    setCaption(textarea.value)
  }

  useEffect(() => {
    if (captionRef.current) {
      adjustTextareaHeight(captionRef.current)
    }
  }, [caption])

  const generateCaption = async () => {
    if (!prompt) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, insira um prompt para gerar o texto.",
        variant: "destructive",
      })
      setTouched(prev => ({ ...prev, prompt: true }))
      return
    }

    setIsGenerating(true)
    try {
      const response = await supabase.functions.invoke("generate-instagram-caption", {
        body: { prompt },
      })

      if (response.error) throw response.error
      setCaption(response.data.generatedText)
      
      toast({
        title: "Sucesso",
        description: "Texto gerado com sucesso!",
      })
    } catch (error) {
      console.error("Erro ao gerar texto:", error)
      toast({
        title: "Erro",
        description: "Erro ao gerar texto. Tente novamente.",
        variant: "destructive",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const validateForm = () => {
    const errors = []
    setTouched({ image: true, prompt: true })

    if (!image) {
      errors.push("Selecione uma imagem")
    }
    if (!prompt) {
      errors.push("Insira um prompt para gerar o texto")
    }
    if (!caption) {
      errors.push("Gere um texto para a postagem")
    }

    if (errors.length > 0) {
      toast({
        title: "Campos obrigatórios",
        description: errors.join(", "),
        variant: "destructive",
      })
      return false
    }
    return true
  }

  const publishPost = async () => {
    if (!validateForm()) return

    setIsPublishing(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("Usuário não encontrado")

      const formData = new FormData()
      formData.append("file", image!)
      formData.append("caption", caption)
      formData.append("userId", user.id)
      
      const response = await supabase.functions.invoke("upload-to-instagram", {
        body: formData,
      })

      if (response.error) throw response.error

      toast({
        title: "Sucesso",
        description: "Post enviado com sucesso! Aguarde a publicação no Instagram.",
      })

      setImage(null)
      setImagePreview(null)
      setPrompt("")
      setCaption("")
      setTouched({ image: false, prompt: false })
      
      queryClient.invalidateQueries({ queryKey: ["instagram-posts"] })
    } catch (error) {
      console.error("Erro ao publicar:", error)
      toast({
        title: "Erro",
        description: "Erro ao enviar post. Tente novamente.",
        variant: "destructive",
      })
    } finally {
      setIsPublishing(false)
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Nova Postagem</h2>
      
      <div className={touched.image && !image ? "ring-2 ring-destructive rounded-lg" : ""}>
        <ImageUpload
          image={image}
          imagePreview={imagePreview}
          onImageChange={(file) => {
            setImage(file)
            setTouched(prev => ({ ...prev, image: true }))
          }}
          onImagePreviewChange={setImagePreview}
        />
        {touched.image && !image && (
          <p className="text-sm text-destructive mt-1">Imagem é obrigatória</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Prompt para Geração de Texto <span className="text-destructive">*</span>
        </label>
        <Textarea
          value={prompt}
          onChange={handleTextareaChange}
          placeholder="Ex: Gere uma legenda envolvente sobre..."
          className={`w-full min-h-[100px] resize-none overflow-hidden ${
            touched.prompt && !prompt ? "border-destructive" : ""
          }`}
        />
        {touched.prompt && !prompt && (
          <p className="text-sm text-destructive mt-1">Prompt é obrigatório</p>
        )}
        <Button
          onClick={generateCaption}
          disabled={isGenerating || !prompt}
          className="mt-2"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Gerando...
            </>
          ) : (
            "Gerar Texto"
          )}
        </Button>
      </div>

      {caption && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Texto Gerado <span className="text-destructive">*</span>
          </label>
          <Textarea
            ref={captionRef}
            value={caption}
            onChange={handleCaptionChange}
            className="w-full min-h-[100px] resize-none overflow-hidden"
            rows={4}
          />
        </div>
      )}

      <Button
        onClick={publishPost}
        disabled={isPublishing}
        className="w-full"
      >
        {isPublishing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Enviar para Publicação
          </>
        )}
      </Button>
    </div>
  )
}